import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PauseCircle, PlayCircle, Info, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserPreferences } from '@/hooks/useUserPreferences';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface AccountRestrictionSectionProps {
  preferences: UserPreferences | null;
  saving: boolean;
  onRestrict: (reason: string) => Promise<boolean>;
  onUnrestrict: () => Promise<boolean>;
}

export const AccountRestrictionSection: React.FC<AccountRestrictionSectionProps> = ({
  preferences,
  saving,
  onRestrict,
  onUnrestrict,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleRestrict = async () => {
    if (!reason.trim()) return;
    
    setProcessing(true);
    const success = await onRestrict(reason);
    setProcessing(false);
    
    if (success) {
      setDialogOpen(false);
      setReason('');
    }
  };

  const handleUnrestrict = async () => {
    setProcessing(true);
    await onUnrestrict();
    setProcessing(false);
  };

  const isRestricted = preferences?.account_restricted;

  return (
    <Card className={isRestricted ? 'border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PauseCircle className="h-5 w-5" />
          Restrict Account
        </CardTitle>
        <CardDescription>
          Temporarily pause all data processing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isRestricted ? (
          <>
            <Alert className="border-amber-500/50 bg-amber-100 dark:bg-amber-900/20">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <strong>Your account is restricted.</strong> Processing is limited to essential storage only.
              </AlertDescription>
            </Alert>

            {preferences?.restriction_reason && (
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Reason:</Label>
                <p className="text-sm p-3 bg-muted rounded-md">{preferences.restriction_reason}</p>
              </div>
            )}

            {preferences?.restriction_requested_at && (
              <p className="text-xs text-muted-foreground">
                Since: {new Date(preferences.restriction_requested_at).toLocaleDateString()}
              </p>
            )}

            <Button 
              onClick={handleUnrestrict} 
              disabled={processing || saving}
              variant="outline"
              className="w-full"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing Restrictions...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Remove Restrictions
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              When restricted, your profile will be hidden, papers made private, and only essential 
              storage will continue. You can lift restrictions at any time.
            </p>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full">
                  <PauseCircle className="h-4 w-4 mr-2" />
                  Restrict My Account
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Restrict Account Processing</DialogTitle>
                  <DialogDescription>
                    Please provide a reason for restricting your account.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="restriction-reason">Reason</Label>
                    <Textarea
                      id="restriction-reason"
                      placeholder="e.g., I need time to verify the accuracy of my data..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleRestrict} 
                    disabled={!reason.trim() || processing}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Restricting...
                      </>
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </CardContent>
    </Card>
  );
};
